import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { ChatCircleText, PaperPlaneTilt, WarningCircle, CurrencyGbp } from "@phosphor-icons/react";

function StatBox({ label, value, sub }) {
  return (
    <div className="brand-card p-4">
      <div className="text-xs text-[var(--text-muted)]">{label}</div>
      <div className="font-display font-extrabold text-2xl text-[var(--brand-green)] mt-1">{value}</div>
      {sub && <div className="text-xs text-[var(--text-muted)] mt-0.5">{sub}</div>}
    </div>
  );
}

const CAT_LABELS = {
  customer_first_quote: "Customer · first quote",
  customer_quote_accepted: "Customer · quote accepted",
  trader_job_alert: "Trader · job alert",
  trader_cap_reached: "Trader · cap-reached nudge",
  trader_job_alert_deferred_quiet: "Skipped · quiet hours",
};

export default function AdminSMSTab() {
  const [spend, setSpend] = useState(null);
  const [recent, setRecent] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const [s, r] = await Promise.all([
          api.get("/admin/sms/spend"),
          api.get("/admin/sms/recent?limit=50"),
        ]);
        setSpend(s.data);
        setRecent(r.data || []);
      } catch (e) {
        setErr("Failed to load SMS data");
      } finally { setLoading(false); }
    })();
  }, []);

  if (loading) return <p className="text-[var(--text-muted)] mt-6">Loading SMS spend…</p>;
  if (err) return <p className="text-red-600 mt-6" data-testid="sms-tab-error">{err}</p>;
  if (!spend) return null;

  return (
    <div className="mt-6 space-y-6" data-testid="admin-sms-tab">
      <div className="brand-card p-5">
        <div className="flex items-center gap-2">
          <ChatCircleText size={20} weight="duotone" className="text-[var(--brand-orange)]" />
          <h2 className="font-display font-extrabold text-xl text-[var(--brand-green)]">SMS Spend Tracker</h2>
        </div>
        <p className="text-sm text-[var(--text-muted)] mt-1">Live cost tracking for Twilio SMS sends. Priority-tier traders capped at 100 SMS/month per user.</p>
      </div>

      <div>
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[var(--text-muted)] mb-2">Today</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
          <StatBox label="Sent today" value={spend.today.count} sub={`£${spend.today.cost_gbp.toFixed(2)} cost`} />
          <StatBox label="Sent (last 30 days)" value={spend.last_30d.count} sub={`£${spend.last_30d.cost_gbp.toFixed(2)} cost`} />
          <StatBox label="All-time" value={spend.all_time.count} sub={`£${spend.all_time.cost_gbp.toFixed(2)} cost`} />
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[var(--text-muted)] mb-2">Breakdown (last 30 days)</h3>
        <div className="brand-card p-4">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-stone)]">
                <th className="py-2">Category</th>
                <th className="py-2 text-right">Sent</th>
                <th className="py-2 text-right">Cost</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(spend.last_30d.by_category).length === 0 ? (
                <tr><td colSpan={3} className="py-4 text-center text-[var(--text-muted)]">No SMS sent yet.</td></tr>
              ) : (
                Object.entries(spend.last_30d.by_category).map(([cat, v]) => (
                  <tr key={cat} className="border-b border-[var(--border-stone)] last:border-0">
                    <td className="py-2">{CAT_LABELS[cat] || cat}</td>
                    <td className="py-2 text-right font-semibold">{v.count}</td>
                    <td className="py-2 text-right">£{v.cost_gbp.toFixed(2)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div>
        <h3 className="font-display font-bold text-sm uppercase tracking-wider text-[var(--text-muted)] mb-2">Last 50 sends</h3>
        <div className="brand-card overflow-hidden">
          {recent.length === 0 ? (
            <p className="p-6 text-center text-[var(--text-muted)]">No SMS in log yet.</p>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="text-left text-[var(--text-muted)] border-b border-[var(--border-stone)] bg-[#FAF8F2]">
                    <th className="py-2 px-3">When</th>
                    <th className="py-2 px-3">To</th>
                    <th className="py-2 px-3">Category</th>
                    <th className="py-2 px-3">Status</th>
                    <th className="py-2 px-3 text-right">Cost</th>
                  </tr>
                </thead>
                <tbody>
                  {recent.map((r, i) => (
                    <tr key={i} className="border-b border-[var(--border-stone)] last:border-0">
                      <td className="py-2 px-3 whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="py-2 px-3 font-mono text-xs">{r.to}</td>
                      <td className="py-2 px-3">{CAT_LABELS[r.category] || r.category}</td>
                      <td className="py-2 px-3">
                        <span className={
                          r.status === "sent" ? "text-[#0A7048] font-semibold" :
                          r.status === "failed" ? "text-red-600 font-semibold" :
                          "text-[var(--text-muted)]"
                        }>{r.status}</span>
                      </td>
                      <td className="py-2 px-3 text-right">£{Number(r.cost_gbp || 0).toFixed(3)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
